/**
 * The status chip, and the "AGRUPADA" tag beside it.
 *
 * Every status is **colour + icon + text** (design spec §2) — a colour-blind
 * reader, a greyscale printout and a screen reader all get the same answer. The
 * tone/icon pair comes from `deca/estado.ts`, never from the call site.
 *
 * `AGRUPADA` is a tag, not a state: a grouped DeCA is still `EN CURSO` or
 * `MODIFICADO`, so it renders as a second chip rather than replacing the first.
 */

import { useTranslation } from 'react-i18next'
import type { DecaEstado, DeliverySummary, VoidReason } from '@deca/api'

import { Chip } from './primitives.tsx'
import { Icon, type IconName } from './Icon.tsx'
import { deliveryProgress } from '../deca/delivery.ts'
import { estadoKey, estadoStyle, type EstadoIcon } from '../deca/estado.ts'

const ICON: Record<EstadoIcon, IconName> = {
  draft: 'draft',
  inTransit: 'truck',
  edited: 'edit',
  check: 'checkCircle',
  clock: 'clock',
  ban: 'ban',
}

export function EstadoChip({
  estado,
  voidReason,
}: {
  estado: DecaEstado
  voidReason?: VoidReason | null
}) {
  const { t } = useTranslation()
  const style = estadoStyle(estado)
  return (
    <span className="row wrap" style={{ gap: 'var(--space-sm)' }}>
      <Chip tone={style.tone} icon={ICON[style.icon]} muted={style.muted}>
        {t(estadoKey(estado))}
      </Chip>
      {/* The cause of an ANULADO is not decoration: superseded and cancelled
          behave differently on the public download path, so the reason wraps
          inside a narrow table cell rather than being clipped away. */}
      {estado === 'ANULADO' && voidReason ? (
        <span className="small muted" style={{ whiteSpace: 'normal' }}>
          {t(`estado.voidReason.${voidReason}`)}
        </span>
      ) : null}
    </span>
  )
}

export function GroupedTag({ count }: { count?: number }) {
  const { t } = useTranslation()
  return (
    <span className="chip chip--neutral chip--outline">
      <Icon name="group" size={14} />
      {t('estado.grouped')}
      {count != null ? <span className="tabular"> · {count}</span> : null}
    </span>
  )
}

/**
 * A document a linked phone created (#110). Colour, icon and text, the rule §2 sets for every tag
 * here. Beside the number rather than in a column of its own: it is a fact about where the
 * document came from, read once, not something to sort a list by.
 */
export function FromAppTag() {
  const { t } = useTranslation()
  return (
    // Neutral, like `GroupedTag` beside it: `info` is the *Viajeros* chip in the next column.
    <span className="chip chip--neutral chip--outline">
      <Icon name="phone" size={14} />
      {t('marks.fromAppTag')}
    </span>
  )
}

/**
 * How far a goods DeCA is delivered (#160): «Entregado» when a driver has confirmed every one of its
 * consignments, «Entregado 1/3» while some are, and nothing otherwise — the rule is
 * `deliveryProgress`. A tag beside the estado, not an estado. The confirmation that leaves every
 * consignment confirmed also completes a document in service (#179: Point 5 of Addendum No. 4
 * replaced Clause 1.3(d) of Addendum No. 3), but the tag is counted from `delivery`, never from the
 * estado. A delivered document can be `CADUCADO`, or still `EN CURSO` or `MODIFICADO` when its
 * completion failed (the confirmation is kept) or when an amendment delivered it by dropping its
 * unconfirmed consignments, which an API caller can do and the portal and ASG Mobile cannot, since
 * both send the whole list back.
 * Outlined, so it is not read as the filled `FINALIZADO` chip.
 */
export function DeliveredTag({
  delivery,
  consignmentCount,
}: {
  delivery: DeliverySummary | null | undefined
  consignmentCount: number | null | undefined
}) {
  const { t } = useTranslation()
  const progress = deliveryProgress(delivery, consignmentCount)
  if (!progress) return null
  return progress.kind === 'delivered' ? (
    <span className="chip chip--success chip--outline">
      <Icon name="check" size={14} />
      {t('marks.delivered')}
    </span>
  ) : (
    <span className="chip chip--neutral chip--outline">
      <Icon name="check" size={14} />
      {t('marks.deliveredPartly', { confirmed: progress.confirmed, total: progress.total })}
    </span>
  )
}

/**
 * Whether an **external** DeCA's PDF has been copied here, or the record is a pointer only.
 *
 * A tag rather than an `EstadoChip`: these documents are not ours and have no estado at all —
 * no CSV, no version chain, no lifecycle. What they do have is the one distinction the whole
 * surface exists for. BOE Second.4 puts the one-year retention duty on both parties, and BOE
 * Third.5 lets the issuing party switch its URL off seven days after the service; a stored URL
 * with no copy behind it therefore stops discharging the duty long before the year is out. So
 * the two cases are drawn apart — colour **and** icon **and** text, the same rule §2 sets for
 * the status chip — in the list, not folded away into the detail.
 */
export function ArchivedCopyTag({ archived }: { archived: boolean }) {
  const { t } = useTranslation()
  return archived ? (
    <span className="chip chip--success">
      <Icon name="checkCircle" size={14} />
      {t('externalDecas.archived')}
    </span>
  ) : (
    <span className="chip chip--warning">
      <Icon name="alert" size={14} />
      {t('externalDecas.linkOnly')}
    </span>
  )
}
